import { isApiError } from "@colorglo/contracts/client";
import {
  Button,
  Checkbox,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@colorglo/ui/components";
import { ActionRemove, blockerIcons, iconSize } from "@colorglo/ui/icons";
import { useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { toast } from "sonner";
import { type BlockerView, useDeleteBlocker } from "../../api/hooks.ts";
import { useAddTimeOff, useStaffTimeOff } from "../../api/settings.ts";
import { useLocale, usePlural, useT } from "../../lib/locale.ts";
import { keys } from "../../lib/query.ts";
import { formatDayHeading, Temporal, today } from "../../lib/time.ts";
import { SettingsCard, SettingsError, SettingsLoading, SettingsSection } from "./shell.tsx";

const KINDS = ["vacation", "sick", "training"] as const;
type Kind = (typeof KINDS)[number];

interface Stretch {
  readonly ids: readonly string[];
  readonly kind: string;
  readonly from: Temporal.PlainDate;
  readonly to: Temporal.PlainDate;
  readonly start_time: string | null;
  readonly end_time: string | null;
  readonly reason: string | null;
  readonly read_only: boolean;
}

function stretches(blockers: readonly BlockerView[]): Stretch[] {
  const result: Stretch[] = [];
  for (const blocker of blockers) {
    const date = Temporal.PlainDate.from(blocker.date);
    const last = result.at(-1);
    if (
      last !== undefined &&
      !blocker.read_only &&
      !last.read_only &&
      last.kind === blocker.kind &&
      last.start_time === blocker.start_time &&
      last.end_time === blocker.end_time &&
      last.reason === blocker.reason &&
      Temporal.PlainDate.compare(last.to.add({ days: 1 }), date) === 0
    ) {
      result[result.length - 1] = { ...last, ids: [...last.ids, blocker.id], to: date };
    } else {
      result.push({
        ids: [blocker.id],
        kind: blocker.kind,
        from: date,
        to: date,
        start_time: blocker.start_time,
        end_time: blocker.end_time,
        reason: blocker.reason,
        read_only: blocker.read_only,
      });
    }
  }
  return result;
}

export function StaffTimeOff({
  franchiseId,
  resourceId,
  timezone,
  editable,
}: {
  readonly franchiseId: string;
  readonly resourceId: string;
  readonly timezone: string;
  readonly editable: boolean;
}) {
  const t = useT();
  const now = today(timezone);
  const list = useStaffTimeOff(
    franchiseId,
    resourceId,
    now.subtract({ years: 1 }).toString(),
    now.add({ years: 1 }).toString(),
  );

  if (list.isPending) return <SettingsLoading />;
  if (list.isError) return <SettingsError onRetry={() => void list.refetch()} />;

  const all = stretches(
    list.data.data.filter((entry) => (KINDS as readonly string[]).includes(entry.kind)),
  );
  const upcoming = all.filter((entry) => Temporal.PlainDate.compare(entry.to, now) >= 0);
  const past = all.filter((entry) => Temporal.PlainDate.compare(entry.to, now) < 0).reverse();

  return (
    <SettingsSection
      heading={t("settings.staff.time_off.heading")}
      body={t("settings.staff.time_off.body")}
    >
      {editable && <AddTimeOff franchiseId={franchiseId} resourceId={resourceId} start={now} />}

      <SettingsCard>
        <StretchList
          heading={t("settings.staff.time_off.upcoming")}
          entries={upcoming}
          franchiseId={franchiseId}
          resourceId={resourceId}
          editable={editable}
        />
        <StretchList
          heading={t("settings.staff.time_off.past")}
          entries={past}
          franchiseId={franchiseId}
          resourceId={resourceId}
          editable={false}
        />
      </SettingsCard>
    </SettingsSection>
  );
}

function AddTimeOff({
  franchiseId,
  resourceId,
  start,
}: {
  readonly franchiseId: string;
  readonly resourceId: string;
  readonly start: Temporal.PlainDate;
}) {
  const t = useT();
  const id = useId();
  const add = useAddTimeOff(franchiseId, resourceId);

  const [kind, setKind] = useState<Kind>("vacation");
  const [from, setFrom] = useState(start.toString());
  const [until, setUntil] = useState(start.toString());
  const [wholeDay, setWholeDay] = useState(true);
  const [startTime, setStartTime] = useState("08:00");
  const [endTime, setEndTime] = useState("12:00");
  const [reason, setReason] = useState("");

  const timed = kind === "training" || !wholeDay;
  const validRange =
    from !== "" &&
    until !== "" &&
    until >= from &&
    Temporal.PlainDate.from(from).until(Temporal.PlainDate.from(until)).days < 120;

  const onAdd = () => {
    add.mutate(
      {
        kind,
        date: from,
        until,
        ...(timed ? { start_time: startTime, end_time: endTime } : {}),
        ...(reason === "" ? {} : { reason }),
      },
      {
        onSuccess: () => {
          toast.success(t("settings.staff.time_off.added"));
          setReason("");
        },
        onError: (error) =>
          toast.error(
            isApiError(error) && error.error.code === "VALIDATION_FAILED"
              ? t("settings.staff.time_off.invalid_range")
              : t("settings.error"),
          ),
      },
    );
  };

  return (
    <SettingsCard>
      <h3 className="text-h3">{t("settings.staff.time_off.add")}</h3>

      <div className="grid max-w-2xl gap-5 sm:grid-cols-3">
        <Field>
          <FieldLabel htmlFor={`${id}-kind`}>{t("calendar.blocker.kind")}</FieldLabel>
          <Select value={kind} onValueChange={(next) => setKind(next as Kind)}>
            <SelectTrigger id={`${id}-kind`} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {KINDS.map((entry) => (
                <SelectItem key={entry} value={entry}>
                  {t(`calendar.blocker.${entry}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field>
          <FieldLabel htmlFor={`${id}-from`}>{t("settings.staff.time_off.from")}</FieldLabel>
          <Input
            id={`${id}-from`}
            type="date"
            value={from}
            onChange={(event) => {
              setFrom(event.target.value);
              if (until < event.target.value) setUntil(event.target.value);
            }}
          />
        </Field>

        <Field>
          <FieldLabel htmlFor={`${id}-until`}>{t("settings.staff.time_off.to")}</FieldLabel>
          <Input
            id={`${id}-until`}
            type="date"
            min={from}
            aria-invalid={!validRange || undefined}
            value={until}
            onChange={(event) => setUntil(event.target.value)}
          />
          {!validRange && <FieldError>{t("settings.staff.time_off.invalid_range")}</FieldError>}
        </Field>
      </div>

      {kind === "training" ? (
        <p className="text-body-sm text-muted-foreground">
          {t("settings.staff.time_off.training_hint")}
        </p>
      ) : (
        <Field orientation="horizontal">
          <Checkbox
            id={`${id}-whole`}
            checked={wholeDay}
            onCheckedChange={(next) => setWholeDay(next === true)}
          />
          <FieldLabel htmlFor={`${id}-whole`} className="font-normal">
            {t("calendar.blocker.whole_day")}
          </FieldLabel>
        </Field>
      )}

      {timed && (
        <div className="flex flex-wrap gap-5">
          <Field className="w-32">
            <FieldLabel htmlFor={`${id}-start`}>{t("settings.staff.time_off.start")}</FieldLabel>
            <Input
              id={`${id}-start`}
              type="time"
              value={startTime}
              onChange={(event) => setStartTime(event.target.value)}
            />
          </Field>
          <Field className="w-32">
            <FieldLabel htmlFor={`${id}-end`}>{t("settings.staff.time_off.end")}</FieldLabel>
            <Input
              id={`${id}-end`}
              type="time"
              value={endTime}
              onChange={(event) => setEndTime(event.target.value)}
            />
          </Field>
        </div>
      )}

      <Field className="max-w-2xl">
        <FieldLabel htmlFor={`${id}-reason`}>{t("settings.staff.time_off.reason")}</FieldLabel>
        <Input
          id={`${id}-reason`}
          maxLength={500}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </Field>

      <div>
        <Button
          onClick={onAdd}
          disabled={!validRange || (timed && endTime <= startTime) || add.isPending}
        >
          {t("settings.staff.time_off.add")}
        </Button>
      </div>
    </SettingsCard>
  );
}

function StretchList({
  heading,
  entries,
  franchiseId,
  resourceId,
  editable,
}: {
  readonly heading: string;
  readonly entries: readonly Stretch[];
  readonly franchiseId: string;
  readonly resourceId: string;
  readonly editable: boolean;
}) {
  const t = useT();
  const plural = usePlural();
  const locale = useLocale();
  const client = useQueryClient();
  const remove = useDeleteBlocker(franchiseId);

  const onRemove = async (stretch: Stretch) => {
    try {
      for (const blockerId of stretch.ids) await remove.mutateAsync(blockerId);
      toast.success(t("settings.staff.time_off.removed"));
    } catch {
      toast.error(t("settings.error"));
    }
    void client.invalidateQueries({ queryKey: keys.staffActivity(franchiseId, resourceId) });
  };

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-h3">{heading}</h3>
      {entries.length === 0 ? (
        <p className="text-body-sm text-muted-foreground">{t("settings.staff.time_off.empty")}</p>
      ) : (
        <ul className="flex flex-col">
          {entries.map((entry) => {
            const Icon = blockerIcons[entry.kind as keyof typeof blockerIcons];
            const days = entry.from.until(entry.to).days + 1;
            const range =
              days === 1
                ? formatDayHeading(entry.from, locale)
                : `${formatDayHeading(entry.from, locale)} – ${formatDayHeading(entry.to, locale)}`;
            return (
              <li
                key={entry.ids[0]}
                className="flex flex-wrap items-center gap-3 border-b border-border py-3 last:border-0"
              >
                {Icon !== undefined && <Icon {...iconSize.inline} aria-hidden="true" />}
                <span className="text-body font-medium">
                  {t(`calendar.blocker.${entry.kind}` as "calendar.blocker.vacation")}
                </span>
                <span className="text-body-sm tabular-nums">{range}</span>
                <span className="text-body-sm text-muted-foreground">
                  {entry.start_time === null
                    ? plural("settings.staff.time_off.days", days)
                    : `${entry.start_time.slice(0, 5)}–${(entry.end_time ?? "").slice(0, 5)}`}
                </span>
                {entry.reason !== null && (
                  <span className="text-body-sm text-muted-foreground">{entry.reason}</span>
                )}
                {editable && !entry.read_only && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-auto"
                    disabled={remove.isPending}
                    onClick={() => void onRemove(entry)}
                  >
                    <ActionRemove {...iconSize.inline} aria-hidden="true" />
                    {t("calendar.blocker.remove")}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
